import { DeleteConfirmationProvider, ConfirmationProvider, DIProvider, AuthProvider, SettingsProvider, SelectedUnitProvider, SectionProvider, FileViewerProvider, RestrictionsProvider, ThemeProvider, ToastProvider, useRestrictions, useServices } from '@/app/providers';
import { LoadingScreen } from '@/core/ui/components/LoadingScreen';
import { ErrorBoundary } from '@/core/sentry';
import { AbilityProvider, isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import type { Permission } from '@/modules/identity/application/ability';
import { PATH_PERMISSIONS, NAV_ITEMS } from '@/shared/navigation';
import { safeStorage } from '@/shared/utils/safeStorage';
import { AccessControl } from '@/pages/AccessControl';
import { Cameras } from '@/pages/Cameras';
import { AdminPanel } from '@/pages/AdminPanel';
import { Amenities } from '@/pages/Amenities';
import { Announcements } from '@/pages/Announcements';
import { Dashboard } from '@/pages/Dashboard';
import { Directory } from '@/pages/Directory';
import { Documents } from '@/pages/Documents';
import { Emergency } from '@/pages/Emergency';
import { Incidents } from '@/pages/Incidents';
import { Notifications } from '@/pages/Notifications';

import { Payments } from '@/pages/Payments';
import CuentaRestringida from '@/pages/CuentaRestringida';
import { CobranzaPage } from '@/modules/cobranza/ui/pages/CobranzaPage';
import { Polls } from '@/pages/Polls';
import { TransparencyPage } from '@/modules/transparency/ui/pages';
import { Profile } from '@/pages/Profile';
import { Reports } from '@/pages/Reports';
import { Restrictions } from '@/pages/Restrictions';
import { Logbook } from '@/pages/Logbook';
import { Patrol } from '@/pages/Patrol';
import { Suppliers } from '@/pages/Suppliers';
import { Inventory } from '@/pages/Inventory';
import { AccessLogs } from '@/pages/AccessLogs';
import { GuardRoutesPage } from '@/modules/guard';
import { MainLayout } from '@/presentation/layouts/MainLayout';
import { ImpersonationBanner } from '@/presentation/components/molecules/ImpersonationBanner';
import { LayoutWrapper } from '@/presentation/layouts/LayoutWrapper';
import { AdminDashboardV2 } from '@/pages/admin-v2/Dashboard';
import { Cuotas } from '@/pages/admin-v2/Cuotas';
import { ComingSoon } from '@/pages/admin-v2/ComingSoon';
import { Login } from '@/presentation/pages/Login';
import { MagicLinkVerify } from '@/presentation/pages/MagicLinkVerify';
import { FamiliarOnboarding } from '@/presentation/pages/FamiliarOnboarding';
import { ForgotPassword } from '@/presentation/pages/ForgotPassword';
import { ResetPassword } from '@/presentation/pages/ResetPassword';
import { Unauthorized } from '@/presentation/pages/Unauthorized';
import { I18nProvider } from '@/shared/i18n';
import type { MagicLinkVerifyResponse } from '@yawi/types';
import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const ONBOARDING_KEY = 'hommi_onboarding_completed';

// "Ahora no, recuérdamelo después" del paso de contraseña. Va en sessionStorage
// y no en localStorage a propósito: recargar la página no debe volver a
// preguntar, pero la próxima sesión sí — para eso es "después".
const PASSWORD_PROMPT_SKIPPED_KEY = 'hommi_password_prompt_skipped';

const readPasswordPromptSkipped = (): boolean => {
  try {
    return sessionStorage.getItem(PASSWORD_PROMPT_SKIPPED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: Permission | Permission[];
}

/**
 * Rutas que conserva un residente en Listado Negro. Debe ir en línea con
 * BLACKLIST_ALLOWED_PATHS del backend: aquí solo se evita mostrar pantallas
 * que de todos modos devolverían 403.
 */
const BLACKLIST_ALLOWED_ROUTES = new Set([
  '/cuenta-restringida',
  '/pagos',
  '/perfil',
  '/notificaciones',
  '/login',
]);

const ProtectedRoute = ({ children, permission }: ProtectedRouteProps) => {
  const { currentUser } = useServices();
  const ability = useAbility();
  const location = useLocation();
  const { isBlacklisted, loading: restrictionsLoading } = useRestrictions();

  // Get default path for user
  const getDefaultPath = () => {
    if (currentUser?.tenantRole === 'FAMILIAR') {
      return '/accesos';
    }
    if (ability.can('manage', 'all') || isSectionAdmin(currentUser)) return '/admin-v2';
    // Residente/guardia: el backend les da read Unit (su propia unidad), así que
    // sin esto caían en /admin/unidades y el listado respondía 403.
    if (ability.can('read', 'Dashboard')) return '/';
    if (ability.can('read', 'Unit')) return '/admin/unidades';
    if (ability.can('read', 'ResidentMenu')) return '/admin/residentes';
    if (ability.can('read', 'StaffMenu')) return '/admin/personal';
    if (ability.can('read', 'User')) return '/admin/residentes';
    if (ability.can('read', 'Role')) return '/admin/roles';
    if (ability.can('read', 'AccessDevice')) return '/admin/puntos-acceso';
    if (ability.can('read', 'Settings')) return '/admin/configuracion';
    return '/';
  };

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Listado Negro: choke point único, cubre menú, URL directa, historial y
  // bookmarks. El bloqueo real lo aplica el backend con 403 UNIT_BLACKLISTED.
  if (!restrictionsLoading && isBlacklisted && !BLACKLIST_ALLOWED_ROUTES.has(location.pathname)) {
    return <Navigate to="/cuenta-restringida" replace />;
  }

  const permissions = Array.isArray(permission) ? permission : permission ? [permission] : [];
  if (permissions.length > 0 && !permissions.some((item) => ability.can(item.action, item.subject as any))) {
    return <Navigate to={getDefaultPath()} replace />;
  }

  return <>{children}</>;
};

// Auth layout wrapper (no MainLayout)
const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const ADMIN_AREA_PERMISSIONS: Permission[] = [
  { action: 'read', subject: 'Unit' },
  { action: 'read', subject: 'ResidentMenu' },
  { action: 'read', subject: 'StaffMenu' },
  { action: 'read', subject: 'User' },
  { action: 'read', subject: 'Role' },
  { action: 'read', subject: 'Amenity' },
  { action: 'read', subject: 'Reservation' },
  { action: 'read', subject: 'Charge' },
  { action: 'read', subject: 'Payment' },
  { action: 'read', subject: 'Expense' },
  { action: 'read', subject: 'AccessDevice' },
  { action: 'read', subject: 'AccessDoor' },
  { action: 'read', subject: 'RfidCredential' },
  { action: 'read', subject: 'LicensePlate' },
  { action: 'read', subject: 'Announcement' },
  { action: 'read', subject: 'Notification' },
  { action: 'read', subject: 'NotificationConfig' },
  { action: 'read', subject: 'Settings' },
  { action: 'read', subject: 'AuditLog' },
  { action: 'read', subject: 'Routine' },
  { action: 'manage', subject: 'all' },
];

// Home route: admins land on the new Admin V2 dashboard, everyone else on the classic Dashboard
const HomeRoute = () => {
  const ability = useAbility();
  const { currentUser } = useServices();
  if (ability.can('manage', 'all') || isSectionAdmin(currentUser)) {
    return <Navigate to="/admin-v2" replace />;
  }
  return <LayoutWrapper><Dashboard /></LayoutWrapper>;
};

const AppRoutes = () => {
  const { currentUser, currentTenant, isLoadingUser, systemSettings, loginWithMagicLink, selectTenantForMagicLink } = useServices();
  const ability = useAbility();
  const location = useLocation();
  const navigate = useNavigate();

  const [magicLinkToken, setMagicLinkToken] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [passwordPromptSkipped, setPasswordPromptSkipped] = useState(readPasswordPromptSkipped);

  // Get default path for user
  const getDefaultPath = () => {
    if (currentUser?.tenantRole === 'FAMILIAR') {
      return '/accesos';
    }
    if (currentUser && (ability.can('manage', 'all') || isSectionAdmin(currentUser))) return '/admin-v2';
    // Ver ProtectedRoute: residentes y guardias van al Dashboard, no a /admin/unidades.
    if (currentUser && ability.can('read', 'Dashboard')) return '/';
    if (currentUser && ability.can('read', 'Unit')) return '/admin/unidades';
    if (currentUser && ability.can('read', 'ResidentMenu')) return '/admin/residentes';
    if (currentUser && ability.can('read', 'StaffMenu')) return '/admin/personal';
    if (currentUser && ability.can('read', 'User')) return '/admin/residentes';
    if (currentUser && ability.can('read', 'Role')) return '/admin/roles';
    if (currentUser && ability.can('read', 'AccessDevice')) return '/admin/puntos-acceso';
    if (currentUser && ability.can('read', 'Settings')) return '/admin/configuracion';
    return '/';
  };

  // Check for magic link token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const pathname = window.location.pathname;

    // Check if URL path contains magic-link
    const isMagicLinkPath = pathname.includes('magic-link') ||
                           pathname.includes('auth/verify');

    if (token && isMagicLinkPath) {
      setMagicLinkToken(token);
      // Clean URL without reload
      window.history.replaceState({}, document.title, pathname);
    }
  }, []);

  // Check if FAMILIAR user needs onboarding
  useEffect(() => {
    if (currentUser?.tenantRole === 'FAMILIAR') {
      const onboardingCompleted = safeStorage.getItem(`${ONBOARDING_KEY}_${currentUser.id}`);
      if (!onboardingCompleted) {
        setShowOnboarding(true);
      }
    }
  }, [currentUser]);

  // Get cached branding for loading screen
  const cachedBranding = useMemo(() => {
    try {
      const cached = localStorage.getItem('hommi_settings');
      if (cached) {
        const settings = JSON.parse(cached);
        return settings.branding || {};
      }
    } catch {
      // Ignore parse errors
    }
    return {};
  }, []);

  // Handle magic link verification success
  const handleMagicLinkSuccess = async (response: MagicLinkVerifyResponse) => {
    await loginWithMagicLink(response);
    setMagicLinkToken(null);
    navigate('/', { replace: true });
  };

  // Handle tenant selection for magic link
  const handleTenantSelect = async (userId: string, tenantId: string) => {
    const response = await selectTenantForMagicLink(userId, tenantId);
    setMagicLinkToken(null);
    navigate('/', { replace: true });
    return response;
  };

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    if (currentUser) {
      safeStorage.setItem(`${ONBOARDING_KEY}_${currentUser.id}`, 'true');
    }
    setShowOnboarding(false);
    // Sin esto, "Ahora no" no hacía nada visible: needsPasswordSetup se deriva
    // de currentUser.passwordSetByUser, que sigue en false hasta que el usuario
    // asigne contraseña, así que la pantalla se volvía a renderizar igual.
    setPasswordPromptSkipped(true);
    try {
      sessionStorage.setItem(PASSWORD_PROMPT_SKIPPED_KEY, 'true');
    } catch {
      // Storage bloqueado: el skip dura lo que dure este render tree.
    }
  };

  if (isLoadingUser) {
    return (
      <LoadingScreen
        appName={cachedBranding.appName || systemSettings?.branding?.appName}
        logoUrl={cachedBranding.logoUrl || systemSettings?.branding?.logoUrl}
        primaryColor={cachedBranding.primaryColor || systemSettings?.branding?.primaryColor}
      />
    );
  }

  // Show magic link verification page if token is present
  if (magicLinkToken) {
    return (
      <MagicLinkVerify
        token={magicLinkToken}
        onSuccess={handleMagicLinkSuccess}
        onSelectTenant={handleTenantSelect}
      />
    );
  }

  // Show onboarding for new FAMILIAR users, and/or the "set your password"
  // step for any account invited by magic link that never assigned its own
  // (backend: master_users.password_set_by_user).
  const showIntro = showOnboarding && currentUser?.tenantRole === 'FAMILIAR';
  const needsPasswordSetup = currentUser?.passwordSetByUser === false && !passwordPromptSkipped;
  if (currentUser && (showIntro || needsPasswordSetup)) {
    return (
      <FamiliarOnboarding
        user={currentUser}
        tenant={currentTenant}
        showIntro={showIntro}
        needsPassword={needsPasswordSetup}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={
        currentUser ? <Navigate to={getDefaultPath()} replace /> : <Login />
      } />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />

      {/* Protected routes with MainLayout */}
      <Route path="/" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/']}>
          <HomeRoute />
        </ProtectedRoute>
      } />
      <Route path="/comunicados" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/comunicados']}>
          <LayoutWrapper><Announcements /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/encuestas" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/encuestas']}>
          <LayoutWrapper><Polls /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/transparencia" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/transparencia']}>
          <LayoutWrapper><TransparencyPage /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/documentos" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/documentos']}>
          <LayoutWrapper><Documents /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/cuenta-restringida" element={
        <ProtectedRoute>
          <LayoutWrapper><CuentaRestringida /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/pagos" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/pagos']}>
          <LayoutWrapper><Payments /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/cobranza" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/cobranza']}>
          <LayoutWrapper><CobranzaPage /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/camaras" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/camaras']}>
          <LayoutWrapper><Cameras /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/accesos" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/accesos']}>
          <LayoutWrapper><AccessControl /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/registro-accesos" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/registro-accesos']}>
          <LayoutWrapper><AccessLogs /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/recorridos" element={
        <ProtectedRoute permission={{ action: 'read', subject: 'GuardRoute' }}>
          <LayoutWrapper><GuardRoutesPage /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/incidentes" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/incidentes']}>
          <LayoutWrapper><Incidents /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/emergencias" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/emergencias']}>
          <LayoutWrapper><Emergency /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/restricciones" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/restricciones']}>
          <LayoutWrapper><Restrictions /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/amenidades" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/amenidades']}>
          <LayoutWrapper><Amenities /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/bitacora" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/bitacora']}>
          <LayoutWrapper><Logbook /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/rondines" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/rondines']}>
          <LayoutWrapper><Patrol /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/directorio" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/directorio']}>
          <LayoutWrapper><Directory /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/proveedores" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/proveedores']}>
          <LayoutWrapper><Suppliers /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/inventario" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/inventario']}>
          <LayoutWrapper><Inventory /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/reportes" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/reportes']}>
          <LayoutWrapper><Reports /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute permission={ADMIN_AREA_PERMISSIONS}>
          <LayoutWrapper><AdminPanel /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/perfil']}>
          <LayoutWrapper><Profile /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/notificaciones" element={
        <ProtectedRoute permission={PATH_PERMISSIONS['/notificaciones']}>
          <LayoutWrapper><Notifications /></LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Admin Layout V2 routes */}
      <Route path="/admin-v2" element={
        <ProtectedRoute>
          <LayoutWrapper><AdminDashboardV2 /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/admin-v2/cuotas" element={
        <ProtectedRoute permission={{ action: 'read', subject: 'Charge' }}>
          <LayoutWrapper><Cuotas /></LayoutWrapper>
        </ProtectedRoute>
      } />
      <Route path="/admin-v2/proximamente" element={
        <ProtectedRoute permission={{ action: 'manage', subject: 'all' }}>
          <LayoutWrapper><ComingSoon /></LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Fallback - redirect to home or login */}
      <Route path="*" element={
        currentUser ? <Navigate to={getDefaultPath()} replace /> : <Navigate to="/login" replace />
      } />
      </Routes>
    </>
  );
};

const AppContent = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <DeleteConfirmationProvider>
            <ConfirmationProvider>
            <I18nProvider>
              <DIProvider>
                <AuthProvider>
                  <RestrictionsProvider>
                  <SelectedUnitProvider>
                    <SectionProvider>
                    <SettingsProvider>
                      <FileViewerProvider>
                        <AbilityProvider>
                          <AppRoutes />
                        </AbilityProvider>
                      </FileViewerProvider>
                    </SettingsProvider>
                    </SectionProvider>
                  </SelectedUnitProvider>
                  </RestrictionsProvider>
                </AuthProvider>
              </DIProvider>
            </I18nProvider>
            </ConfirmationProvider>
          </DeleteConfirmationProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
};

export default App;
